"use client";

import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type {
  Confidence,
  FitLabel,
  FootId,
  FootName,
  ScannedLine,
  Variant,
} from "@/lib/tool-kit/poetry/meter";
import { feetText, wordMarks } from "@/lib/tool-kit/poetry/scansion-view";

/** The strings the scansion lines need; the meter widget's strings satisfy this. */
export interface ScansionStrings {
  linesHeading: string;
  lineHeading: string;
  fitPercent: string;
  fitLabels: Record<FitLabel, string>;
  confidenceLine: string;
  confidenceLevels: Record<Confidence, string>;
  footNames: Record<FootName, string>;
  footAdjectives: Record<FootId, string>;
  lengthNames: readonly string[];
  metreName: string;
  variants: Record<Variant, string>;
  substitution: string;
  feetRowLabel: string;
  noteAlternate: string;
  noteExtra: string;
  noteMissing: string;
  noteRival: string;
  legend: string;
  marksLabel: string;
}

export function metreName(
  strings: Pick<
    ScansionStrings,
    "footAdjectives" | "lengthNames" | "metreName"
  >,
  foot: FootId,
  feet: number
): string {
  return t(strings.metreName, {
    foot: strings.footAdjectives[foot],
    length: strings.lengthNames[feet - 1] ?? String(feet),
  });
}

const TONES = {
  none: "text-foreground",
  soft: "rounded bg-amber-200/70 text-foreground dark:bg-amber-400/30",
  hard: "rounded bg-rose-200/70 text-foreground dark:bg-rose-400/30",
} as const;

function LineNotes({
  scanned,
  strings,
}: {
  scanned: ScannedLine;
  strings: ScansionStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const notes: string[] = [];
  const { template, alignment } = scanned.fit;
  for (const { word, reading } of scanned.alternates) {
    notes.push(
      tPlural(strings.noteAlternate, locale, reading.syllables, {
        word: word.text,
        count: fmt(reading.syllables),
      })
    );
  }
  if (template.variant !== "regular") {
    notes.push(strings.variants[template.variant]);
  }
  const missing = alignment.slotSyllable.filter((s) => s === null).length;
  if (alignment.extras.length > 0) {
    notes.push(
      tPlural(strings.noteExtra, locale, alignment.extras.length, {
        count: fmt(alignment.extras.length),
      })
    );
  }
  if (missing > 0) {
    notes.push(
      tPlural(strings.noteMissing, locale, missing, { count: fmt(missing) })
    );
  }
  if (scanned.rival) {
    notes.push(
      t(strings.noteRival, {
        metre: metreName(strings, scanned.rival.foot, scanned.rival.feet),
      })
    );
  }
  if (notes.length === 0) {
    return null;
  }
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground text-xs">
      {notes.map((note) => (
        <li key={note}>{note}</li>
      ))}
    </ul>
  );
}

function LineCard({
  scanned,
  number,
  strings,
}: {
  scanned: ScannedLine;
  number: number;
  strings: ScansionStrings;
}) {
  const fmt = useToolNumber();
  const words = wordMarks(scanned);
  return (
    <li className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-medium text-foreground text-sm">
          {t(strings.lineHeading, { number: fmt(number) })}
          <span className="ml-2 font-normal text-muted-foreground">
            {metreName(
              strings,
              scanned.fit.template.foot,
              scanned.fit.template.feet
            )}
          </span>
        </h4>
        <p className="text-foreground text-sm">
          <span className="font-semibold">
            {t(strings.fitPercent, {
              percent: fmt(Math.round(scanned.score * 100)),
            })}
          </span>
          <span className="ml-2 text-muted-foreground">
            {strings.fitLabels[scanned.label]}
          </span>
        </p>
      </div>
      <div
        className="flex flex-wrap gap-x-4 gap-y-2"
        title={strings.marksLabel}
      >
        {words.map((word) => (
          <span
            className="inline-flex flex-col items-center"
            key={`${word.start}-${word.key}`}
          >
            <span className="text-base text-foreground">{word.text}</span>
            <span className="font-mono text-sm tracking-widest">
              {word.marks.map((mark, i) => (
                <span
                  className={TONES[mark.problem]}
                  // biome-ignore lint/suspicious/noArrayIndexKey: a word's syllables are positional
                  key={i}
                >
                  {mark.mark}
                </span>
              ))}
            </span>
          </span>
        ))}
      </div>
      <p className="font-mono text-foreground text-sm">
        <span className="mr-2 font-sans text-muted-foreground text-xs">
          {strings.feetRowLabel}
        </span>
        {feetText(scanned.feet)}
      </p>
      <p className="text-foreground text-xs">
        {scanned.feet
          .map(
            (foot) =>
              strings.footNames[foot.name] +
              (foot.substituted ? ` (${strings.substitution})` : "")
          )
          .join(" · ")}
      </p>
      <p className={HINT}>
        {t(strings.confidenceLine, {
          level: strings.confidenceLevels[scanned.confidence],
        })}
      </p>
      <LineNotes scanned={scanned} strings={strings} />
    </li>
  );
}

export function ScansionLines({
  lines,
  strings,
}: {
  lines: readonly ScannedLine[];
  strings: ScansionStrings;
}) {
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.linesHeading}</h3>
      <p className={HINT}>{strings.legend}</p>
      <ol className="space-y-3">
        {lines.map((scanned, index) => (
          <LineCard
            key={scanned.line.number}
            number={index + 1}
            scanned={scanned}
            strings={strings}
          />
        ))}
      </ol>
    </section>
  );
}
